'use client';

import { useState } from 'react';
import { MessageCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface AskQuestionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (question: string) => Promise<void>;
  isSubmitting?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

const MAX_QUESTION_LENGTH = 500;

export function AskQuestionModal({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: AskQuestionModalProps) {
  const [question, setQuestion] = useState('');
  const charCount = question.length;
  const isOverLimit = charCount > MAX_QUESTION_LENGTH;
  const canSubmit = question.trim().length > 10 && !isOverLimit && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit(question.trim());
    setQuestion('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSubmitting) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setQuestion('');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Задать вопрос основателю
          </DialogTitle>
          <DialogDescription>
            Задайте вопрос о продукте. Основатель получит уведомление и сможет ответить.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            placeholder="Например: Как вы планируете монетизировать продукт?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={isSubmitting}
            className={cn(
              'min-h-[120px] resize-none',
              isOverLimit && 'border-destructive focus-visible:ring-destructive'
            )}
          />
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-tertiary">
              Минимум 10 символов
            </span>
            <span
              className={cn(
                'tabular-nums',
                isOverLimit ? 'text-destructive font-medium' : 'text-foreground-tertiary'
              )}
            >
              {charCount}/{MAX_QUESTION_LENGTH}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Отправка...
              </>
            ) : (
              'Отправить вопрос'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
